import type { MessageFormatElement } from '@formatjs/icu-messageformat-parser';
import { trackError } from '@atlassian/help-center-common-util/analytics';
import { getLanguageFromLocale } from '@atlassian/help-center-common-util/locale-data';
import { reportError } from '@atlassian/help-center-common-util/sentry';

// We need to explicitly type these as inferred types are 'any'
type EditorMessages = Record<string, string> | Record<string, MessageFormatElement[]> | Record<string, never>;

interface EditorModule {
    default: EditorMessages;
}

type EditorMessageModule = EditorModule | undefined;

export const shouldIgnoreError = (locale: string) => {
    const unavailableTranslationsForLocales = {
        'editor-common': ['ro', 'is'],
    };

    for (const [, value] of Object.entries(unavailableTranslationsForLocales)) {
        return value.includes(locale);
    }

    return false;
};

export const importLocaleMessages = async (locale: string) => {
    const packages = ['editor-core', 'emoji', 'editor-common', 'media-ui', 'smart-card'];
    const errors: Record<string, string>[] = [];

    const modulesPromises = packages.map(async (packageName) => {
        try {
            // Does not handle multiple magic comments in a single import statement
            // Suppressing existing violation. Please fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, import/dynamic-import-chunkname
            const module: EditorMessageModule = await import(
                /* webpackInclude: /\.js$/ */
                /* webpackExclude: /\[index|languages]\.js$/ */
                /* webpackChunkName: "[request]" */
                `../../../../node_modules/@atlaskit/${packageName}/dist/esm/i18n/${locale}`
            );
            return module;
        } catch (error) {
            return Promise.reject(error);
        }
    });

    try {
        const modules = await Promise.allSettled(modulesPromises);
        const allMessages: EditorMessages = modules.reduce((messages, module) => {
            let moduleMessages;
            if (module.status === 'fulfilled') {
                moduleMessages = module.value?.default ? module.value?.default : {};
            } else if (module.status === 'rejected') {
                if (module.reason) {
                    // Suppressing existing violation. Please fix this.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    const moduleName =
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        module.reason?.moduleName ? module.reason?.moduleName : 'unknownModule';

                    // Suppressing existing violation. Please fix this.
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    errors.push({ [moduleName]: module.reason?.message ? module.reason?.message : '' });
                } else {
                    errors.push({ unknownModule: 'reason unknown' });
                }
            }

            return { ...messages, ...moduleMessages };
        }, {});

        if (errors.length > 0 && !shouldIgnoreError(locale)) {
            trackError(
                'editor.translations.failed.to.load',
                { message: JSON.stringify(errors) },
                `Editor translations failed to load for locale ${locale}`
            );
        }

        return Promise.resolve(allMessages);
    } catch (error) {
        reportError(`Error in resolving editor translations for ${locale}`, {
            // Suppressing existing violation. Please fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,  @typescript-eslint/no-unsafe-member-access
            message: error?.message ? error?.message : '',
        });

        return Promise.resolve({});
    }
};

export const getNormalizedLocale = (locale: string) => {
    let normalizedLocale = locale;

    if (locale === 'no-NO') {
        normalizedLocale = 'nb';
    } else if (locale === 'pt-BR') {
        normalizedLocale = 'pt_BR';
    } else if (locale === 'pt-PT') {
        normalizedLocale = 'pt_PT';
    } else if (locale === 'en-GB') {
        normalizedLocale = 'en_GB';
    } else {
        normalizedLocale = getLanguageFromLocale(locale);
    }

    return normalizedLocale;
};
